export type ExperienceMode = 'super_admin' | 'player' | 'business_admin' | 'staff';

export const EXPERIENCE_MODES: readonly ExperienceMode[] = ['super_admin', 'player', 'business_admin', 'staff'] as const;

// These modes can show management selectors; business selection can be layered above facility selection later.
export const MANAGEMENT_SCOPED_EXPERIENCE_MODES: readonly ExperienceMode[] = ['super_admin', 'business_admin', 'staff'] as const;

export const EXPERIENCE_MODE_LABELS: Record<ExperienceMode, string> = {
  super_admin: 'Super Admin',
  player: 'Player',
  business_admin: 'Business Admin',
  staff: 'Staff',
};

export const EXPERIENCE_MODE_DESCRIPTIONS: Record<ExperienceMode, string> = {
  super_admin: 'Platform administration',
  player: 'Discovery and bookings',
  business_admin: 'Business management tools',
  staff: 'Operational tools',
};

export interface FacilityContext {
  facilityId: string;
  facilityName: string | null;
  facilitySlug: string | null;
}

export interface FacilityContextSource {
  facilityId: string;
  facilityName: string | null;
  facilitySlug: string | null;
}

export interface AccountModeContext {
  experienceMode: ExperienceMode;
  activeFacility: FacilityContext | null;
}

export interface AccountModeContextInput {
  experienceMode: ExperienceMode;
  activeFacility?: FacilityContext | null;
}

export interface ShellAccountModeContextInput {
  experienceMode: ExperienceMode;
  facilities: ReadonlyArray<FacilityContextSource>;
  selectedFacilityId?: string | null;
}

export interface ShellAccountModeContext extends AccountModeContext {
  facilityCount: number;
  facilitySummaryLabel: string | null;
  hasFacilityContext: boolean;
}

export const createFacilityContext = (input: FacilityContext): FacilityContext => ({
  facilityId: input.facilityId,
  facilityName: input.facilityName ?? null,
  facilitySlug: input.facilitySlug ?? null,
});

export const createAccountModeContext = (input: AccountModeContextInput): AccountModeContext => ({
  experienceMode: input.experienceMode,
  activeFacility: input.activeFacility ? createFacilityContext(input.activeFacility) : null,
});

export const isManagementScopedExperienceMode = (mode: ExperienceMode): boolean =>
  MANAGEMENT_SCOPED_EXPERIENCE_MODES.includes(mode);

export const hasActiveFacilityContext = (context: AccountModeContext): context is AccountModeContext & { activeFacility: FacilityContext } =>
  context.activeFacility !== null;

// Keep the facility selector driven by count, not by privileged role branches.
export const shouldShowFacilitySelector = (facilityCount: number): boolean => facilityCount > 1;

export const resolveShellAccountModeContext = (input: ShellAccountModeContextInput): ShellAccountModeContext => {
  const sortedFacilities = [...input.facilities].sort((left, right) => (left.facilityName ?? '').localeCompare(right.facilityName ?? ''));
  const facilityCount = sortedFacilities.length;
  const selectedFacility =
    input.selectedFacilityId ? sortedFacilities.find((facility) => facility.facilityId === input.selectedFacilityId) ?? null : null;
  const activeFacility = selectedFacility ?? (facilityCount === 1 ? createFacilityContext(sortedFacilities[0]) : null);

  return {
    experienceMode: input.experienceMode,
    activeFacility,
    facilityCount,
    facilitySummaryLabel:
      activeFacility?.facilityName ?? (facilityCount === 0 ? null : facilityCount === 1 ? sortedFacilities[0].facilityName ?? 'Business' : `${facilityCount} businesses`),
    hasFacilityContext: facilityCount > 0,
  };
};

